import { create } from 'zustand';
import { UserActivityType, ResourceType } from '../pages/RecommendationFeed/types/activity_types';

interface RecentUserActivity {
  resource_id: string;
  resource_type: ResourceType;
  activity_type: UserActivityType;
}

// Track collapsing/hidden state for resources
interface CollapsingResource {
  resourceId: string;
  isCollapsing: boolean;
  isHidden: boolean;
}

interface RecommendationFeedState {
  // Hidden channels functionality
  hiddenChannels: string[];
  hideChannel: (channelId: string) => void;
  isChannelHidden: (channelId: string) => boolean;
  unhideChannel: (channelId: string) => void;
  clearHiddenChannels: () => void;
  
  // User activity tracking
  recentUserActivities: RecentUserActivity[];
  setRecentUserActivities: (activities: RecentUserActivity[]) => void;
  addUserActivity: (activity: RecentUserActivity) => void;
  removeUserActivity: (resourceId: string, activityType: UserActivityType) => void;
  toggleUserActivity: (activity: RecentUserActivity) => void;
  
  // Helper methods for checking activity states
  isResourceBookmarked: (resourceId: string) => boolean;
  isResourceLiked: (resourceId: string) => boolean;
  
  // Collapsing/hiding resources functionality
  collapsingResources: CollapsingResource[];
  setResourceCollapsing: (resourceId: string, isCollapsing: boolean) => void;
  setResourceHidden: (resourceId: string, isHidden: boolean) => void;
  isResourceCollapsing: (resourceId: string) => boolean;
  isResourceHidden: (resourceId: string) => boolean;
  startCollapsingResource: (resourceId: string) => void;
  hideResource: (resourceId: string) => void;
}

export const useRecommendationFeedStore = create<RecommendationFeedState>()((set, get) => ({
  // Hidden channels functionality
  hiddenChannels: [],
  
  hideChannel: (channelId: string) => {
    set((state) => ({
      hiddenChannels: [...state.hiddenChannels, channelId]
    }));
  },
  
  isChannelHidden: (channelId: string) => {
    return get().hiddenChannels.includes(channelId);
  },
  
  unhideChannel: (channelId: string) => {
    set((state) => ({
      hiddenChannels: state.hiddenChannels.filter(id => id !== channelId)
    }));
  },
  
  clearHiddenChannels: () => {
    set({ hiddenChannels: [] });
  },
  
  // User activity tracking
  recentUserActivities: [],
  
  setRecentUserActivities: (activities: RecentUserActivity[]) => {
    set({ recentUserActivities: activities });
  },
  
  addUserActivity: (activity: RecentUserActivity) => {
    // Just use the snake_case properties directly
    const normalizedActivity = {
      resource_id: activity.resource_id,
      resource_type: activity.resource_type,
      activity_type: activity.activity_type
    };
    
    set((state) => {
      // Remove any existing activities of the same type for the same resource
      const filteredActivities = state.recentUserActivities.filter(
        a => !(a.resource_id === normalizedActivity.resource_id && a.activity_type === normalizedActivity.activity_type)
      );
      
      // Add the new activity
      return {
        recentUserActivities: [...filteredActivities, normalizedActivity]
      };
    });
  },
  
  removeUserActivity: (resource_id: string, activity_type: UserActivityType) => {
    set((state) => ({
      recentUserActivities: state.recentUserActivities.filter(
        a => !(a.resource_id === resource_id && a.activity_type === activity_type)
      )
    }));
  },
  
  toggleUserActivity: (activity: RecentUserActivity) => {
    // Use snake_case properties directly
    const resource_id = activity.resource_id;
    const activity_type = activity.activity_type;
    
    const isActive = get().recentUserActivities.some(
      a => a.resource_id === resource_id && a.activity_type === activity_type
    );
    
    if (isActive) {
      // If activity exists, remove it
      get().removeUserActivity(resource_id, activity_type);
    } else {
      // If activity doesn't exist, add it
      get().addUserActivity(activity);
    }
  },
  
  // Helper methods for checking activity states
  isResourceBookmarked: (resource_id: string) => {
    return get().recentUserActivities.some(
      activity => activity.resource_id === resource_id && activity.activity_type === UserActivityType.BOOKMARKED
    );
  },
  
  isResourceLiked: (resource_id: string) => {
    return get().recentUserActivities.some(
      activity => activity.resource_id === resource_id && activity.activity_type === UserActivityType.LIKED
    );
  },
  
  // Collapsing/hiding resources functionality
  collapsingResources: [],
  
  setResourceCollapsing: (resourceId: string, isCollapsing: boolean) => {
    set((state) => {
      // Check if resource already exists in the array
      const existingIndex = state.collapsingResources.findIndex(r => r.resourceId === resourceId);
      
      if (existingIndex >= 0) {
        // Update existing resource
        const updatedResources = [...state.collapsingResources];
        updatedResources[existingIndex] = {
          ...updatedResources[existingIndex],
          isCollapsing
        };
        return { collapsingResources: updatedResources };
      } else {
        // Add new resource
        return {
          collapsingResources: [
            ...state.collapsingResources,
            { resourceId, isCollapsing, isHidden: false }
          ]
        };
      }
    });
  },
  
  setResourceHidden: (resourceId: string, isHidden: boolean) => {
    set((state) => {
      // Check if resource already exists in the array
      const existingIndex = state.collapsingResources.findIndex(r => r.resourceId === resourceId);
      
      if (existingIndex >= 0) {
        // Update existing resource
        const updatedResources = [...state.collapsingResources];
        updatedResources[existingIndex] = {
          ...updatedResources[existingIndex],
          isHidden
        };
        return { collapsingResources: updatedResources };
      } else {
        // Add new resource
        return {
          collapsingResources: [
            ...state.collapsingResources,
            { resourceId, isCollapsing: false, isHidden }
          ]
        };
      }
    });
  },
  
  isResourceCollapsing: (resourceId: string) => {
    const resource = get().collapsingResources.find(r => r.resourceId === resourceId);
    return resource ? resource.isCollapsing : false;
  },
  
  isResourceHidden: (resourceId: string) => {
    const resource = get().collapsingResources.find(r => r.resourceId === resourceId);
    return resource ? resource.isHidden : false;
  },
  
  startCollapsingResource: (resourceId: string) => {
    // Set collapsing to true
    get().setResourceCollapsing(resourceId, true);
    
    // After animation completes, set to hidden
    setTimeout(() => {
      get().setResourceHidden(resourceId, true);
    }, 350); // Match this with the CSS animation duration
  },
  
  hideResource: (resourceId: string) => {
    // Set hidden to true after collapsing animation completes
    get().setResourceHidden(resourceId, true);
  }
}));

// For backward compatibility, re-export under the old name
export const useHiddenChannelsStore = useRecommendationFeedStore;
